import { useEffect, useState } from 'react'

import { Card, Divider, Dropdown, Option, Text } from '@joor/design-system'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import { OrderStatus } from '__generated__/atlas-types'

import { useRenderToasters } from 'hooks/useRenderToasters'
import {
  isAccountTypeBrand,
  isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector,
} from 'store/currentUser/selectors'

import { ContactList } from '../ContactList/ContactList'
import {
  EditableContactList,
  EditableContactListFieldIds,
} from '../EditableContactList/EditableContactList'
import { LoadingContacts } from './LoadingContacts'
import { useUpdateOrderContacts } from './contactsList.mutations'
import { useContactsWidgetData } from './contactsWidget.hooks'
import { OrderData } from 'features/Orders/Review/components'
import { ReviewPageIds } from 'features/Orders/Review/review.ids'
import { useOrderId } from 'features/Orders/orders.hooks'

export enum ContactsWidgetTestIds {
  LoadingContacts = 'loading-contacts',
  EditActionButton = 'contacts-edit-button',
  SaveActionButton = 'contacts-save-button',
}

const StyledCard = styled(Card)`
  section {
    max-height: 370px;
    overflow-y: auto;
  }

  &.edit-mode {
    section {
      overflow-y: visible;
    }
  }
`

const StyledDivider = styled(Divider)`
  margin: 8px 0px;
`

const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 5px;
`

export type ContactWidgetType = {
  isEditable?: boolean
  className?: string
  onClickAssignBuyerButton?: () => void
  showAssignBuyerButton?: boolean
  testId?: string
  brand?: OrderData['brand'] | undefined | null
  buyer: OrderData['buyer']
  salesCommissions?: OrderData['salesComissions']
  retailerProfile?: OrderData['retailerProfile']
  status?: OrderData['status']
  salesRepresentative?: OrderData['salesRepresentative']
  loading?: boolean
}

type SavedSalesCommissions = {
  first?: string
  second?: string
  third?: string
}

export const ContactsWidget = ({
  isEditable,
  className = '',
  testId,
  brand,
  buyer,
  salesCommissions,
  salesRepresentative,
  retailerProfile,
  status,
  loading,
}: ContactWidgetType) => {
  const isBrand = useSelector(isAccountTypeBrand)
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )

  const [isEditMode, setIsEditMode] = useState(false)
  const [newBuyerId, setNewBuyerId] = useState<string>()
  const [salesComissionsValues, setSalesComissionsValues] =
    useState<SavedSalesCommissions>()
  const [newSalesRepId, setNewSalesRepId] = useState<string>()

  const orderId = useOrderId()
  const { isUpdating, onUpdate } = useUpdateOrderContacts(orderId)
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()

  const { brandBuyers, brandSalesReps, loadingContacts } =
    useContactsWidgetData(isEditMode, brand, retailerProfile)

  const isEditNotesAndReturnedOrdersRestricted =
    brand?.orderSettings?.restrictEditNotesAndReturnedOrders

  const salesCommissionsOptions = brand?.orderSettings?.salesComissions?.map(
    (salesComissionsOption: string) => {
      return { value: salesComissionsOption, label: salesComissionsOption }
    },
  )

  const restrictEditByOrderStatus =
    (status === OrderStatus.APPROVED || status === OrderStatus.SHIPPED) &&
    isEditApprovedShippedRestricted

  const isRetailerWithRestriction =
    isEditNotesAndReturnedOrdersRestricted && !isBrand

  const canEdit =
    isEditable && !restrictEditByOrderStatus && !isRetailerWithRestriction

  const handleEditAction = (): void => {
    setIsEditMode(true)
  }

  const handleSaveAction = async (): Promise<void> => {
    let orderDetails = {}

    if (newBuyerId && newBuyerId !== buyer?.id) {
      const selectedBuyer = brandBuyers?.edges
        .flatMap((edge) => edge.buyers)
        .find((buyer) => buyer.id === newBuyerId)
      const buyerName = selectedBuyer?.name
      const buyerEmail = selectedBuyer?.email

      orderDetails = {
        ...orderDetails,
        ...(buyerName && { buyerName }),
        ...(buyerEmail && { buyerEmail }),
      }
    }

    if (newSalesRepId && newSalesRepId !== salesRepresentative?.id) {
      orderDetails = {
        ...orderDetails,
        salesRepId: newSalesRepId,
      }
    }

    const salesComissionsValuesSaved = Object.values(
      salesComissionsValues || [],
    ).filter((salesComissionValue) => !!salesComissionValue)

    if (salesComissionsValuesSaved.length) {
      orderDetails = {
        ...orderDetails,
        orderCommissions: salesComissionsValuesSaved,
      }
    }

    if (!isEmpty(orderDetails)) {
      const {
        messages: { error },
      } = await onUpdate({
        orderIds: [orderId],
        orderDetails,
      })
      if (error) {
        renderErrorToast({
          title: 'Unable to update Contacts',
          description: 'An unexpected server error has occurred',
        })
      } else {
        renderSuccessToast({
          title: 'Contacts Updated',
          description: 'Contacts have been successfully updated.',
        })
      }
    }

    setIsEditMode(false)
  }

  const handleSelectContact = (
    field: EditableContactListFieldIds,
    selected: Option,
  ) => {
    if (field === EditableContactListFieldIds.BUYER) {
      setNewBuyerId(selected.value)
    } else if (field === EditableContactListFieldIds.SALES_REP) {
      setNewSalesRepId(selected.value)
    }
  }

  const handleAddBuyer = () => {
    setNewBuyerId(undefined)
  }

  useEffect(() => {
    if (salesCommissions) {
      setSalesComissionsValues({
        first: salesCommissions[0]?.name,
        second: salesCommissions[1]?.name,
        third: salesCommissions[2]?.name,
      })
    }
  }, [salesCommissions])

  if (loading) {
    return (
      <StyledCard
        testId={ContactsWidgetTestIds.LoadingContacts}
        title="Contacts"
        className={className}
      >
        <LoadingContacts />
      </StyledCard>
    )
  }

  return (
    <StyledCard
      title="Contacts"
      className={isEditMode ? `${className} edit-mode` : className}
      actionButtonLabel={isEditMode ? 'Save' : 'Edit'}
      actionButtonTestId={
        isEditMode
          ? ContactsWidgetTestIds.SaveActionButton
          : ContactsWidgetTestIds.EditActionButton
      }
      {...(canEdit && {
        onClickActionButton: isEditMode ? handleSaveAction : handleEditAction,
      })}
      disableActionButton={isUpdating}
      actionButtonId={ReviewPageIds.ContactsEditButtonId}
      testId={testId}
    >
      {canEdit && isEditMode ? (
        <>
          <EditableContactList
            brandBuyers={brandBuyers}
            brandSalesReps={brandSalesReps}
            orderBuyer={buyer || undefined}
            orderSalesRep={salesRepresentative || undefined}
            onSelect={handleSelectContact}
            onAddBuyer={handleAddBuyer}
            disableBuyerDropdown={isUpdating || loadingContacts}
            disableSalesRepDropdown={isUpdating || loadingContacts || !isBrand}
          />
          {isBrand && (
            <>
              <StyledDivider />
              <Text fontSize="small" fontWeight="semibold" color="primary-400">
                Sales Commissions
              </Text>
              <DropdownWrapper>
                <Dropdown
                  value={{
                    label: salesComissionsValues?.first,
                    value: salesComissionsValues?.first,
                  }}
                  onSelect={({ selected }: { selected: Option }): void => {
                    setSalesComissionsValues({
                      ...salesComissionsValues,
                      first: selected.value,
                    })
                  }}
                  options={salesCommissionsOptions}
                />
                <Dropdown
                  value={{
                    label: salesComissionsValues?.second,
                    value: salesComissionsValues?.second,
                  }}
                  onSelect={({ selected }: { selected: Option }): void => {
                    setSalesComissionsValues({
                      ...salesComissionsValues,
                      second: selected.value,
                    })
                  }}
                  options={salesCommissionsOptions}
                />
                <Dropdown
                  value={{
                    label: salesComissionsValues?.third,
                    value: salesComissionsValues?.third,
                  }}
                  onSelect={({ selected }: { selected: Option }): void => {
                    setSalesComissionsValues({
                      ...salesComissionsValues,
                      third: selected.value,
                    })
                  }}
                  options={salesCommissionsOptions}
                />
              </DropdownWrapper>
            </>
          )}
        </>
      ) : (
        <>
          <ContactList
            buyers={buyer ? [buyer] : []}
            salesRepresentatives={
              salesRepresentative ? [salesRepresentative] : []
            }
            onClickAssignBuyerButton={handleEditAction}
            showAssignBuyerButton={canEdit && !buyer}
          />
          {salesCommissions && salesCommissions?.length > 0 && isBrand && (
            <>
              <StyledDivider />
              <Text fontSize="small" fontWeight="semibold" color="primary-400">
                Sales Commissions
              </Text>
              <Text
                breakLines
                fontWeight="medium"
                margin="3px 0 0 0"
                color="primary-600"
                className="notranslate"
              >
                {salesCommissions
                  ?.map(
                    (salesCommission: { name: string }) => salesCommission.name,
                  )
                  .join('\n')}
              </Text>
            </>
          )}
        </>
      )}
    </StyledCard>
  )
}
